import React, { useContext } from 'react';
import { PageContext } from '@/context/PageContext';
import { Metadata } from '@latitude/metadata';
import Layout from '@/components/layout';
import { SITE_URL } from '@/utils/constants';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import CardFilterListRebrand from '@/components/CardFilterListRebrand/CardFilterListRebrand';
import Section from '@latitude/section-rebrand';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import { Accordion } from '@latitude/accordion-rebrand';
import Typography from '@latitude/typography';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrandV2/HowToApplyRebrandV2';
import { Link } from '@latitude/link';
import PageData from '@/data/pages/credit-cards/balance-transfer.json';
import {
  getCardFilterListRebrandDataFromContentful,
  getHowToApplyRebrandV2DataFromContentful
} from '@/utils/creditCardsContentfulData';

const IMPORTANT_INFO = [
  "<sup>*</sup> Balance transfer applies to eligible customer's Australian non-Latitude credit cards only. Balance transfer amounts commencing from $500 or more, up to 85% of your participating Latitude credit card’s credit limit. Monthly payments required (exact amounts specified in your statement). Paying only the minimum monthly payment may not pay out the balance transfer amount before the end of the plan period. If there is an outstanding balance after the plan period, interest will be charged at the cash advance rate, currently 29.99% (subject to change). A once-off balance transfer fee of 3% applies to the amount being transferred. This will be charged to your participating Latitude credit card account when the balance transfer is processed. Offer only available to existing customers who have submitted the application form. Eligibility criteria apply. Subject to approval.<br /><br /> <a href='https://assets.latitudefinancial.com/legals/credit-card-general-terms-and-conditions.pdf'>Latitude General Terms and Conditions</a> set out the T&Cs of the participating Latitude credit card. Credit provided by Latitude Finance Australia ABN 42 008 583 588 Australian Credit Licence number 392145. "
];

const brandedCardFilterListData = {
  id: 'compare-cards',
  heading: 'Choose your credit card',

  cards: [
    {
      type: 'Credit cards',
      category: ['all', 'travel', 'rewards', 'balance-transfer'],
      title: 'Latitude 28° Global Platinum Mastercard',
      productid: 'CCAU28D',

      bgImage: {
        url: '/images/credit-cards/28d-bg-card-img.png',
        name: '28d background image'
      },

      cardArt: {
        url: '/images/credit-cards/cc-28d.webp',
        name: '28d credit card'
      },

      fee: {
        value: '$0',
        label: <>annual fee in your first year (normally $96)</>
      },
      rate: {
        value: '$1000',
        label: 'Minimum Credit Limit'
      },
      description: [
        'Get your annual fee waived when you spend $12,000 on eligible purchases each year.<sup>4</sup>',
        'Enjoy a $0 international transaction fees and great exchange rates set by Mastercard.',
        'Shop and be rewarded with up to $240 in Latitude Rewards<sup>5</sup> when you spend $1,000 or more on eligible international and local purchases each month.',
        'Get 10% off on selected hotels with Expedia and Wotif. Exclusions and T&Cs apply.<sup>6</sup>',
        'Purchase Rate: 28.99%',
        'Cash Advance Rate: 29.99%'
      ],
      ctas: [
        {
          text: 'Apply now',
          url:
            'https://cards.latitudefinancial.com/28degrees?promo_code=M9FNRZ2R&source_code=IDAAA',
          variant: 'primary',
          size: 'lg'
        },
        {
          text: 'Find out more',
          url: '/credit-cards/28-degrees/',
          variant: 'secondary-blue',
          size: 'lg'
        }
      ]
    },

    {
      type: 'Credit cards',
      category: [
        'all',
        'interest-free-plans',
        'balance-transfer',
        'compare-cards'
      ],
      title: 'Latitude Gem Visa',
      productid: 'CCAUGEM',

      bgImage: {
        url: '/images/credit-cards/gem-bg-card-img.webp',
        name: 'gem background image'
      },

      cardArt: {
        url: '/images/credit-cards/cc-gem.webp',
        name: 'gem credit card'
      },

      fee: {
        value: '$11.95',
        label: 'Monthly credit card fee'
      },
      rate: {
        value: '$1000',
        label: <>Minimum Credit Limit</>
      },
      description: [
        'Earn 3% back in Latitude Rewards on eligible spend across groceries, dining, food delivery, movie and theatre ticket purchases – after you spend $1,000 or more on general purchases in a statement period.<sup>1</sup>',
        'Enjoy 6 months Interest Free on your general purchases of $250 or more. Select your purchases in the Latitude App within 30 days. ',
        'Enjoy more time to pay with 0% interest free plans at 100s of retailers online and in-store<sup>3</sup>. No penalties if you pay your plan earlier.',
          '0% p.a. for 26 months on Balance Transfers. 3% Balance Transfer fee and eligibility criteria apply. New customers only, must be approved by 26 February 2027. Balance Transfer must be submitted by 31 March 2027. T&Cs apply<sup>*</sup>.', 
        'Purchase Rate: 28.99% ',
        'Cash Advance Rate: 29.99% '
      ],
      ctas: [
        {
          text: 'Apply now',
          url:
            'https://cards.latitudefinancial.com/gem?source_code=IDAAA&promo_code=X9KUC2YS',
          variant: 'primary',
          size: 'lg'
        },
        {
          text: 'Find out more',
          url: '/credit-cards/gem-visa/',
          variant: 'secondary-blue',
          size: 'lg'
        }
      ]
    },

    {
      type: 'Credit cards',
      category: [
        'all',
        'interest-free-plans',
        'balance-transfer',
        'compare-cards'
      ],
      title: 'Latitude GO Mastercard',
      productid: 'CCAUGOM',

      bgImage: {
        url: '/images/credit-cards/go-bg-card-img.webp',
        name: 'gomc background image'
      },

      cardArt: {
        url: '/images/credit-cards/cc-go.webp',
        name: 'gomc credit card'
      },

      fee: {
        value: '$11.95',
        label: 'Monthly credit card fee'
      },
      rate: {
        value: '$1000',
        label: <>Minimum Credit Limit</>
      },
      description: [
        'Long term Interest Free Plans at Harvey Norman<sup>®</sup> and other fantastic retailers.<sup>3</sup>',
        'Enjoy 0% interest for 6 months on your general purchases of $250 or more. Select your purchases in the Latitude App within 30 days.',
        '0% p.a. for 26 months on Balance Transfers. 3% Balance Transfer fee and eligibility criteria apply. New customers only, must be approved by 26 February 2027. Balance Transfer must be submitted by 31 March 2027. T&Cs apply<sup>*</sup>.',
        'Up to 55 days<sup>2</sup> interest free on general purchases.',
        'Purchase Rate: 28.99%',
        'Cash Advance Rate: 29.99%'
      ],
      ctas: [
        {
          text: 'Apply now',
          url:
            'https://cards.latitudefinancial.com/go?promo_code=P3VEDHGC&source_code=IDAAA',
          variant: 'primary',
          size: 'lg'
        },
        {
          text: 'Find out more',
          url: '/credit-cards/gomastercard/',
          variant: 'secondary-blue',
          size: 'lg'
        }
      ]
    }
  ]
};

const HOW_TO_APPLY_DATA = {
  id: 'how-to-apply',
  image: '/images/credit-cards/bt-how-to-apply.png',
  howToApply: {
    sectionTitle: 'How to apply for the Balance Transfer offer',
    items: [
      {
        title: (
          <>
            <b>1. Apply for an eligible credit card</b>
          </>
        ),
        description: (
          <>
            Choose the credit card that best fits your needs then complete the
            online application form. You'll need to apply for the Balance
            Transfer separately after your card is approved.
            <br />
            <br />
            If you are <b>an existing Latitude customer</b>, you can apply for
            0% p.a. for 12 months Balance Transfer now by logging into the
            Latitude App or the Latitude Service Centre.{' '}
            <a href="/credit-cards/generic-balance-transfer" target="_self">
              Click here
            </a>{' '}
            to find out more.{' '}
          </>
        )
      },
      {
        title: (
          <>
            <b>2. Start your Balance Transfer using the Latitude App</b>
          </>
        ),
        description: (
          <>
            <ul>
              <li>
                Once your credit card application is approved, open the Latitude
                App (make sure you're on the latest version) or log in via the
                Latitude Service Centre.
              </li>
              <li>Log in using your Latitude ID.</li>
              <li>Go to the Balance Transfer tab and complete the form.</li>
            </ul>
            Please also note, we can't process Balance Transfers from certain
            issuers - see our indicative list.<sup>#</sup>
          </>
        )
      },
      {
        title: (
          <>
            <b>3. Don't have a Latitude ID?</b>
          </>
        ),
        description: (
          <>
            Simply register from the login page and follow the prompts. Then
            proceed to the Balance Transfer tab and follow the steps above.
          </>
        )
      },
      {
        title: (
          <>
            <b>4. Terms and Conditions</b>
          </>
        ),
        description: (
          <>
            For more information,{' '}
            <Link
              href="https://assets.latitudefinancial.com/legals/terms-conditions/balance-transfer-termsconds.pdf"
              trackEventData={{
                label: 'click-here',
                location: 'how-to-apply-terms-and-conditions'
              }}
            >
              click here.
            </Link>
          </>
        )
      }
    ]
  }
};

const FREQUENTLY_ASKED_QUESTIONS = [
  {
    q: 'What fees apply to a balance transfer?',
    a: (
      <>
        You will need to pay a once-off balance transfer fee of 3% of the
        balance transfer amount or in accordance with the specific balance
        transfer offer when you take up a promotional offer. This fee is charged
        to your participating Latitude credit card account when your balance
        transfer is processed.
      </>
    )
  },
  {
    q: 'What is my balance transfer limit?',
    a: (
      <>
        The minimum amount is $500 and your total balance transfer amount cannot
        exceed 85% of your credit limit (excluding any applicable balance
        transfer fee charged).
        <br />
        <br />
        For example, if your credit limit is $10,000, the maximum balance
        transfer amount is $8,500. If you have a current balance of $3,000, this
        amount is deducted from $8,500 and as a result your maximum balance
        transfer amount is $5,500.
      </>
    )
  },
  {
    q: 'How much will I have to pay each month?',
    a: (
      <>
        Your monthly statement will specify the minimum amount you will need to
        pay each month. However, if you only make the minimum monthly payment,
        you may not pay out the balance transfer amount before the end of the
        promotional period.
      </>
    )
  },
  {
    q:
      'If I want to make extra or larger payments than the minimum monthly payment toward my balance transfer, how do I do that?',
    a: (
      <>
        You can make extra or larger payments directly toward your balance
        transfer at any time. Open the Latitude App or log in to the Latitude
        Service Centre, go to My Plans, and choose your Balance Transfer. From
        there, enter the amount you want to pay and complete the payment.
      </>
    )
  },
  {
    q:
      'What happens if I have outstanding balance at the end of the promotional period?',
    a: (
      <>
        If there is an outstanding balance after the promotional period,
        interest will be charged at the cash advance rate, currently 29.99%
        (subject to change).
      </>
    )
  },
  {
    q:
      'Does Latitude arrange for the other credit card account(s) to be closed?',
    a: (
      <>
        No. Once Latitude has paid the balance on your other credit card
        account(s), it's your choice whether you close that card account(s).
        Remember, it's a good idea to close other credit card account(s) to
        clear or reduce your credit card debt.
      </>
    )
  },
  {
    q:
      'How long does it take for a balance transfer to be processed to my Latitude credit card account?',
    a: (
      <>
        Balance transfers can take up to 10 business days to process depending
        on the institution(s) to which the payments are being made. Please
        notify us if the balance transfer does not appear on your statement of
        nominated account.
      </>
    )
  },
  {
    q:
      'Can I still use my current Latitude credit card for interest free purchases?',
    a: (
      <>
        If your Latitude credit card has interest free payment plans available
        for purchases, then you can still choose to make these purchases up to
        your available credit limit, after the balance transfer transaction is
        processed.
      </>
    )
  }
];

function CCBalanceTransferPage({ location }) {
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  const cardFilterListData =  
    getCardFilterListRebrandDataFromContentful(
      state?.cardFilterListRebrandData?.[0]
    ) || brandedCardFilterListData;
  const howToApplyData =
    getHowToApplyRebrandV2DataFromContentful(
      state?.howToApplyRebrandV2Data?.[0]
    ) || HOW_TO_APPLY_DATA;

  return (
    <Layout location={location}>
      <main>
        <Metadata
          title={'Credit Card Balance Transfers | Latitude'}
          description={
            'Visit the Latitude Service Centre or Latitude App to apply.'
          }
          keywords="balance transfer"
          canonical={`${SITE_URL}${location.pathname}`}
        />

        <HeroV2
          {...(state?.heroBannerData?.[0] ||
            PageData?.contentful?.heroBannerData?.[0])}
        />

        <CardFilterListRebrand {...cardFilterListData} />

        <HowToApply
          data={howToApplyData}
          background={howToApplyData?.background}
        />

        <Section
          id="faq"
          css={`
            background-color: ${REBRAND_COLOR.WHITE};
            padding-top: ${PADDING.P32};
            padding-bottom: ${PADDING.P32};
          `}
        >
          <div
            data-contentful={state?.faqData?.[0]?.contentfulID}
            css={`
              display: flex;
              flex-direction: column;
              gap: 32px;
            `}
          >
            <Typography.H4>Frequently Asked Questions</Typography.H4>
            <AnalyticsLocationProvider location={'frequently asked questions'}>
              <Accordion
                css={`
                  .bg-black-10 {
                    padding: 16px 24px;
                  }
                `}
                items={state?.faqData?.[0]?.data || FREQUENTLY_ASKED_QUESTIONS}
              />
            </AnalyticsLocationProvider>
          </div>
        </Section>
        <ImportantInformation
          data={{
            content: {
              importantInformationSectionOne: IMPORTANT_INFO
            }
          }}
          css={`
            overflow-x: hidden;
            .row {
              @media (min-width: 1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }
          `}
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
      </main>
    </Layout>
  );
}

export default CCBalanceTransferPage;
